import React, { useState } from 'react';
import { MapPin, Mail, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    console.log('Form submitted:', formData);
    toast({
      title: 'Messaggio inviato!',
      description: 'Ti risponderemo al più presto. Grazie per averci contattato.'
    });
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#081221] to-[#0d63ce]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-serif text-6xl md:text-7xl font-bold text-white mb-6">Contatti</h1>
          <p className="text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
            Siamo qui per rispondere alle tue domande e discutere delle tue esigenze
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-xl">
              <h2 className="font-serif text-4xl font-bold text-[#081221] mb-6">
                Invia un Messaggio
              </h2>
              <p className="text-gray-600 mb-8">
                Compila il form e ti risponderemo entro 24 ore lavorative.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome e Cognome *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0d63ce] focus:border-transparent"
                      placeholder="Mario Rossi"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0d63ce] focus:border-transparent"
                      placeholder="mario.rossi@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Azienda
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0d63ce] focus:border-transparent"
                      placeholder="Nome azienda"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefono
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0d63ce] focus:border-transparent"
                      placeholder="+39 123 456 7890"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Oggetto *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0d63ce] focus:border-transparent"
                    placeholder="Richiesta informazioni"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Messaggio *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0d63ce] focus:border-transparent resize-none"
                    placeholder="Scrivi qui il tuo messaggio..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-[#081221] text-white rounded-lg font-semibold hover:bg-[#0d63ce] transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Invia Messaggio</span>
                  <Send size={20} />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-serif text-4xl font-bold text-[#081221] mb-6">
                  Informazioni di Contatto
                </h2>
                <p className="text-xl text-gray-700 leading-relaxed mb-8">
                  Raggiungi il nostro team per qualsiasi domanda su progetti, servizi o
                  collaborazioni.
                </p>
              </div>

              <div className="space-y-6">
                {/* Office Address */}
                <div className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-lg">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#081221] text-lg mb-2">Sede Legale</h3>
                    <p className="text-gray-700">
                      Via Giuseppe Garibaldi, 43<br />
                      05100 Terni (TR), Italia
                    </p>
                    <p className="text-gray-500 text-sm mt-2">P.IVA/C.F.: 01619830555</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-lg">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#081221] text-lg mb-2">Email</h3>
                    <p className="text-gray-700">info@ternienergia.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">Come Raggiungerci</h2>
            <p className="text-xl text-gray-600">La nostra sede a Terni, nel cuore dell'Italia</p>
          </div>

          <div className="bg-gray-200 rounded-2xl h-96 flex items-center justify-center overflow-hidden\">
            <iframe 
              src="https://maps.google.com/maps?q=Via+Giuseppe+Garibaldi+43+Terni&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mappa Sede TerniEnergia Progetti"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
